import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import { Stack, TextButton } from 'library/ui';
import { client, queryStringifyObject } from 'shared';
import { useUserContext } from 'UserContext';
import { PromptForReviewModal } from 'parent-portal/shared/UserActions/PromptForReviewModal';

import { LocationStates, WorkerModalShell } from '../shared';
import Header from './Header';
import LocationsList from './LocationsList';
import { BannedWorkerModalContext } from './BannedWorkerModalContext';

import type { BusinessLocationBannedWorker, ContextBusiness, ShiftProviderUser } from 'models';

export default function BanWorkerModal({
    open,
    onSuccess,
    onClose,
    provider,
    businessLocationId,
    leaveFeedback,
}: {
    open: boolean;
    onSuccess: (bannedWorkers: BusinessLocationBannedWorker[], prompt: boolean) => void;
    onClose: () => void;
    provider: ShiftProviderUser;
    businessLocationId?: number;
    leaveFeedback?: boolean;
}) {
    const { user } = useUserContext();
    const history = useHistory();

    const [businessLocation, setBusinessLocation] = useState<{ id: number; name: string; business: { id: number } }>();
    const [business, setBusiness] = useState<ContextBusiness | undefined>(user?.businesses_active?.[0]);
    const [locations, setLocations] = useState<BusinessLocationBannedWorker[]>([]);
    const [empty, setEmpty] = useState(false);
    const [error, setError] = useState(false);
    const [promptForReview, setPromptForReview] = useState(false);
    const [triggerClick, setTriggerClick] = useState(false);

    const businessIdNumber: number | undefined = business?.id ?? businessLocation?.business?.id;

    useEffect(() => {
        if (open) {
            if (businessIdNumber && provider.id) {
                client(
                    `api/business-outlaws/locations-and-statuses/?business_id=${businessIdNumber}&business_outlaw_id=${provider.id}`,
                    {
                        method: 'GET',
                    },
                )
                    .then((response: BusinessLocationBannedWorker[]) => {
                        setLocations(response);
                        response?.length === 0 ? setEmpty(true) : setBusiness(response[0].business);
                        setError(false);
                    })
                    .catch(() => setError(true));
            } else {
                setError(true);
            }
        }
    }, [businessIdNumber, provider.id, open]);

    useEffect(() => {
        if (open && businessLocationId) {
            client(`api/business-location-overview/${businessLocationId}/`, { method: 'GET' })
                .then(setBusinessLocation)
                .catch(() => setError(true));
        }
    }, [businessLocationId, open]);

    function onBanSuccess(bannedWorkers: BusinessLocationBannedWorker[]) {
        if (user?.admin) {
            onSuccess(bannedWorkers, false);
        }
    }

    async function prePrompt() {
        const reviewParams = queryStringifyObject({
            business_id: businessIdNumber,
            user_id: provider.id,
            sentiment: 'permissive_block',
            check_changed: false.toString(),
        });

        const shouldPrePrompt = await client(`api/provider-review/prompt-for-review/?${reviewParams}`)
            .then((response: { prompt: boolean }) => {
                setPromptForReview(response.prompt);
                return response.prompt;
            })
            .catch(() => {
                setPromptForReview(false);
                return false;
            });

        return shouldPrePrompt;
    }

    if (!open) {
        return null;
    }

    const hasError = !user || !businessIdNumber || error;

    return (
        <>
            <WorkerModalShell
                title={`Request not to book ${provider.first_name}`}
                error={hasError}
                onClose={() => {
                    setError(false);
                    onClose();
                }}
            >
                {!hasError && (
                    <BannedWorkerModalContext.Provider
                        value={{
                            isAdmin: user?.admin ?? false,
                            business: business ?? {
                                id: businessIdNumber,
                                name: businessLocation?.name ?? `Business (${businessIdNumber})`,
                            },
                            provider,
                            emptyLocationList: empty,
                            locations,
                            businessLocationId,
                            triggerClick,
                            setTriggerClick,
                            setLocations,
                            setError,
                            prePrompt,
                            onSuccess: onBanSuccess,
                            onClose,
                        }}
                    >
                        <Stack gap="md">
                            <Header />
                            <LocationStates loading={!empty && !locations?.length} empty={empty}>
                                <LocationsList />
                            </LocationStates>
                            {leaveFeedback && (
                                <TextButton onClick={() => history.push(`/providers/review/${provider.id}`)}>
                                    Leave feedback about {provider.first_name}
                                </TextButton>
                            )}
                        </Stack>
                    </BannedWorkerModalContext.Provider>
                )}
            </WorkerModalShell>
            {business?.id && (
                <PromptForReviewModal
                    open={promptForReview}
                    onClose={() => {
                        setPromptForReview(false);
                        setTriggerClick(true);
                    }}
                    userId={provider?.id}
                    promptedByActionType="block"
                />
            )}
        </>
    );
}
